import { useState } from 'react';

import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { Avatar } from '@components/avatar';
import { useLocalStorageState } from '@components/component-helpers';
import { Columns, ColumnSettings, ColumnSort, DataGrid } from '@components/data-grid';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyState } from '@components/empty-state';
import { IconApp } from '@components/icons/app';
import { IconOnboarding } from '@components/icons/onboarding';
import { IconSubIndicator } from '@components/icons/sub-indicator';
import { ProgressBar } from '@components/progress-bar';
import { Stack } from '@components/stack';
import { Tooltip } from '@components/tooltip';
import { theme } from '@styles/theme';
import { abbreviateDate } from '@utils/date';

import { Box } from 'components/Box';
import { useStore } from 'models/Provider';
import {
  ApplicationTrigger,
  ApplicationTriggerStatus,
  OnboardApplication,
} from 'types/applications';

import { useOnboardingContext, useOnboardingFilterState } from './onboarding-config';

type Props = {
  data: Array<OnboardApplication>;
  sort: ColumnSort<OnboardApplication>;
  onSortChange: (newSort: ColumnSort<OnboardApplication>) => void;
  isCompletedSection: boolean;
};

type ApplicantProp = {
  applicant: OnboardApplication;
};

export const OnboardingList = ({ data, sort, onSortChange, isCompletedSection }: Props) => {
  const { t } = useStore();
  const search = useOnboardingFilterState((state) => state.debouncedSearch);
  const { filters } = useOnboardingContext();
  const hasFilters = filters.state.appliedFilters.length > 0;

  const [columns] = useState<Columns<OnboardApplication>>([
    {
      label: t('onboarding.applicants.label.name'),
      key: 'name',
      sortable: true,
      render: (a) => <ApplicantInfo applicant={a} />,
    },
    {
      label: t('onboarding.applicants.label.hired'),
      key: 'dateOfHire',
      sortable: true,
      render: (a) => <HiredDate applicant={a} />,
    },
    {
      label: t('onboarding.applicants.label.job'),
      key: 'jobTitle',
      sortable: true,
      render: (a) => <Title applicant={a} />,
    },
    {
      label: t('onboarding.applicants.label.status'),
      key: 'isHiredTriggers',
      sortable: false,
      render: (applicant) => <Status applicant={applicant} />,
    },
  ]);

  const [settings, setSettings] = useLocalStorageState<ColumnSettings<OnboardApplication>>({
    key: 'onboarding-columns',
    defaultValue: {
      name: { order: 0, visible: true },
      dateOfHire: { order: 1, visible: true },
      jobTitle: { order: 2, visible: true },
      isHiredTriggers: { order: 3, visible: true },
    },
  });

  if (!data.length) {
    if (search) {
      return <EmptySearchState hasFilters={hasFilters} />;
    }
    return (
      <EmptyState
        icon={<IconOnboarding />}
        header={t(
          isCompletedSection ? 'onboarding.empty.completed.header' : 'onboarding.empty.header',
        )}
        content={t(
          isCompletedSection ? 'onboarding.empty.completed.content' : 'onboarding.empty.content',
        )}
      />
    );
  }

  return (
    <DataGrid
      label="Onboarding list"
      columns={columns}
      sort={sort}
      onSortChange={onSortChange}
      columnSettings={settings}
      onColumnSettingsChange={setSettings}
      data={data}
      renderExpandedRow={(applicant) => <ExpandedRow applicant={applicant} />}
    />
  );
};

const getTriggerKey = (trigger: ApplicationTrigger) => `${trigger.id}-${trigger.name}`;

const Status = ({ applicant }: ApplicantProp) => {
  const { t } = useStore();
  return (
    <div>
      <Box flex h="56" alignItems="center">
        {applicant.sortedIsHiredTriggers.map((trigger) => (
          <Box mr="4" key={getTriggerKey(trigger)}>
            <Tooltip header={trigger.name} content={t(trigger.statusTranslationKey)}>
              <Dot status={trigger.status} data-html={true} />
            </Tooltip>
          </Box>
        ))}
      </Box>
    </div>
  );
};

const HiredDate = ({ applicant }: ApplicantProp) => {
  const { lang } = useStore();
  return (
    <Box flex h="56" alignItems="center">
      {applicant.dateOfHire && abbreviateDate(applicant.dateOfHire, lang)}
    </Box>
  );
};

const Title = ({ applicant }: ApplicantProp) => (
  <Box flex h="56" alignItems="center">
    <ActionText.Link font="regular14" color="purple100" to={`/jobs/${applicant.jobId}/`}>
      {applicant.jobTitle}
    </ActionText.Link>
  </Box>
);

const ApplicantInfo = ({ applicant }: ApplicantProp) => {
  return (
    <Box flex h="56" alignItems="center">
      <Box flex alignItems="center">
        <Avatar name={applicant.name} src={applicant.picture} size="md" />
        <Box ml="8">
          <ActionText.Link
            font="regular14"
            color="purple100"
            to={`/jobs/${applicant.jobId}/onboarding/${applicant.id}`}
          >
            {applicant.name}
          </ActionText.Link>
        </Box>
      </Box>
    </Box>
  );
};

type TriggerProps = {
  jobLink: string;
  ratio: number;
  trigger: ApplicationTrigger;
  isChild?: boolean;
};

const Trigger = ({ jobLink, ratio, trigger, isChild }: TriggerProps) => {
  const { t } = useStore();

  return (
    <Box h={24} flex alignItems="center" color="purple50">
      <TriggerBox w={240} mr={20}>
        <ActionText.Link font="regular14" color="purple100" to={jobLink}>
          <Stack space={2} align="center" orientation="horizontal">
            {isChild ? <IconSubIndicator color="purple50" /> : <IconApp color="purple50" />}
            {trigger.name}
          </Stack>
        </ActionText.Link>
      </TriggerBox>
      <Box w={240} m={8}>
        <ProgressBar ratio={ratio} />
      </Box>
      <Box ml={8} flex alignItems="center">
        <Dot small status={trigger.status} />
        <StatusText status={trigger.status}>{t(trigger.statusTranslationKey)}</StatusText>
      </Box>
    </Box>
  );
};

const statusToRatio = (trigger: ApplicationTrigger) =>
  trigger.status === 'completed'
    ? 1
    : trigger.status === 'in_progress'
      ? trigger.ratio === null
        ? 0.5
        : trigger.ratio
      : 0;

const ExpandedRow = ({ applicant }: ApplicantProp) => (
  <Box mb={16} ml={40}>
    {applicant.sortedIsHiredTriggers.map((trigger) => {
      const jobLink = `/jobs/${applicant.jobId}/onboarding/${applicant.id}/integrations/${trigger.id}`;
      const ratio = statusToRatio(trigger);

      return (
        <div key={getTriggerKey(trigger)}>
          <Trigger ratio={ratio} jobLink={jobLink} trigger={trigger} />
          {trigger.children?.map((child) => (
            <Trigger
              key={child.name}
              ratio={child.ratio}
              jobLink={jobLink}
              trigger={child as ApplicationTrigger}
              isChild
            />
          ))}
        </div>
      );
    })}
  </Box>
);

const StatusText = styled.div<Pick<ApplicationTrigger, 'status'>>((p) => ({
  marginLeft: theme.space[8],
  color:
    p.status === 'completed'
      ? theme.color.green100
      : p.status === 'in_progress'
        ? theme.color.orange100
        : 'inherit',
}));

const TriggerBox = styled(Box)((p) => ({
  color: theme.color.purple50,
  ':hover': {
    color: theme.color.blue100,
  },
}));

type DotProps = { status: ApplicationTriggerStatus; small?: boolean };

export const Dot = styled.div<DotProps>((p) => ({
  background:
    p.status === 'completed'
      ? theme.color.green100
      : p.status === 'in_progress'
        ? theme.color.orange100
        : theme.color.purple20,
  width: p.small ? '0.5rem' : '1rem',
  height: p.small ? '0.5rem' : '1rem',
  borderRadius: '50%',
}));
